import { Link, useLocation, useNavigate } from "react-router-dom";
import { Gauge, Timer, Smartphone, Trophy, Activity, LogOut, Menu, X, LayoutDashboard, ShieldCheck, Globe, ChevronDown, ChevronRight, Bug, Network, Code2, Globe2, Search, FileSearch, FolderOpen, GitCompareArrows, Waves, Rocket, Settings, House, FileText, Download } from "lucide-react";
import { useState, useEffect } from "react";
import { useAuth } from "@/context/AuthContext";
import { useI18n, LANGS } from "@/i18n";
import { cn } from "@/lib/utils";
import { ThemeToggle } from "@/components/ThemeToggle";
import { AccessibilityPanel } from "@/components/AccessibilityPanel";
import { MENU, PL, pickLang } from "@/components/panel/labels";
import { usePublicPages } from "@/lib/publicPages";

function Avatar({ user }) {
  if (user.picture) {
    return <img src={user.picture} alt={user.name} data-testid="user-avatar" className="h-8 w-8 rounded-full border border-border object-cover" />;
  }
  return (
    <span data-testid="user-avatar" className="flex h-8 w-8 items-center justify-center rounded-full bg-primary/10 font-600 text-primary">
      {(user.name || "?").charAt(0).toUpperCase()}
    </span>
  );
}

const LANG_NAMES = {
  en: "English", pl: "Polski", de: "Deutsch", es: "Español", pt: "Português", fr: "Français",
  it: "Italiano", tr: "Türkçe", ar: "العربية", hi: "हिन्दी", zh: "中文", ja: "日本語", ko: "한국어", ru: "Русский",
};

const LANG_FLAGS = {
  en: "🇬🇧", pl: "🇵🇱", de: "🇩🇪", es: "🇪🇸", pt: "🇵🇹", fr: "🇫🇷",
  it: "🇮🇹", tr: "🇹🇷", ar: "🇸🇦", hi: "🇮🇳", zh: "🇨🇳", ja: "🇯🇵", ko: "🇰🇷", ru: "🇷🇺",
};

function LangSwitch({ align = "right" }) {
  const { lang, stripLang, t } = useI18n();
  const loc = useLocation();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const base = stripLang(loc.pathname);
  const go = (l) => {
    localStorage.setItem("ns_lang", l);
    setOpen(false);
    const target = l === "en" ? base : (base === "/" ? `/${l}` : `/${l}${base}`);
    nav(target + loc.search);
  };
  return (
    <div className="relative" data-testid="lang-switch">
      <button
        onClick={() => setOpen((o) => !o)}
        data-testid="lang-switch-btn"
        aria-label={t("nav.language")}
        aria-haspopup="listbox"
        aria-expanded={open}
        className="flex items-center gap-1.5 rounded-full border border-border px-3 py-1.5 text-xs font-600 text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
      >
        <Globe className="h-4 w-4" />
        <span className="uppercase">{lang}</span>
        <ChevronDown className={cn("h-3.5 w-3.5 transition-transform", open && "rotate-180")} />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className={cn("absolute z-50 mt-2 max-h-72 w-44 overflow-y-auto rounded-2xl border border-border bg-popover p-1.5 shadow-xl", align === "left" ? "left-0" : "right-0")}>
            {LANGS.map((l) => (
              <button
                key={l}
                data-testid={`lang-${l}`}
                onClick={() => go(l)}
                className={cn(
                  "flex w-full items-center justify-between rounded-xl px-3 py-2 text-sm font-600 transition-colors",
                  lang === l ? "bg-primary text-primary-foreground" : "text-foreground hover:bg-accent"
                )}
              >
                <span className="flex items-center gap-2"><span>{LANG_FLAGS[l]}</span> {LANG_NAMES[l] || l}</span>
                <span className="text-xs uppercase opacity-60">{l}</span>
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function NavDropdown({ id, label, icon: Icon, items, loc, lp }) {
  const [o, setO] = useState(false);
  const act = items.some((x) => loc.pathname === lp(x.to));
  return (
    <div className="relative">
      <button onClick={() => setO((v) => !v)} data-testid={`nav-${id}-btn`} aria-haspopup="menu" aria-expanded={o}
        className={cn("flex items-center gap-1.5 whitespace-nowrap rounded-full px-2.5 py-2 text-[13px] font-500 leading-none transition-colors", act ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-accent hover:text-foreground")}>
        <Icon className="h-4 w-4 shrink-0" /> {label} <ChevronDown className={cn("h-3.5 w-3.5 transition-transform", o && "rotate-180")} />
      </button>
      {o && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setO(false)} />
          <div className="absolute left-0 z-50 mt-2 w-56 rounded-2xl border border-border bg-popover p-1.5 shadow-xl" data-testid={`nav-${id}-menu`}>
            {items.map((n) => {
              const active = loc.pathname === lp(n.to);
              return (
                <Link key={n.to} to={lp(n.to)} onClick={() => setO(false)} data-testid={`nav-${n.to.slice(1)}`}
                  className={cn("flex items-center gap-2 rounded-xl px-3 py-2 text-sm font-500 transition-colors", active ? "bg-primary text-primary-foreground" : "text-foreground hover:bg-accent")}>
                  <n.icon className="h-4 w-4 shrink-0" /> {n.label}
                </Link>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}

export function Navbar() {
  const { user, logout } = useAuth();
  const { t, lp, lang } = useI18n();
  const loc = useLocation();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const pick = (m) => pickLang(m, lang);
  const customPages = usePublicPages();
  const pageItems = (where) => customPages
    .filter((p) => (p.placements || []).includes(where))
    .map((p) => ({ to: `/p/${p.slug}`, label: p.title, icon: FileText, custom: true }));

  const widgetLabel = { pl: "Widget na stronę", en: "Website widget", de: "Website-Widget", es: "Widget web", pt: "Widget para site", fr: "Widget pour site", it: "Widget per sito", tr: "Site widget'ı", ru: "Виджет для сайта", ar: "أداة للموقع", hi: "वेबसाइट विजेट", zh: "网站小组件", ja: "サイト用ウィジェット", ko: "웹사이트 위젯" }[lang] || "Website widget";

  // Close the mobile menu when the user scrolls the page.
  useEffect(() => {
    if (!open) return;
    const onScroll = () => setOpen(false);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [open]);

  const NET = [
    { to: "/test", label: t("nav.speed"), icon: Gauge },
    { to: "/stability", label: t("nav.stability"), icon: Timer },
    { to: "/mobile", label: t("nav.mobile"), icon: Smartphone },
    { to: "/bufferbloat", label: t("nav.bloat"), icon: Activity },
    { to: "/rankings", label: t("nav.ranking"), icon: Trophy },
  ];
  const TOOLS = [
    { to: "/website-speed", label: pick(PL.siteSpeed), icon: Globe2 },
    { to: "/sites/compare", label: t("nav.compare"), icon: GitCompareArrows },
    { to: "/sites/catalog", label: t("nav.saved"), icon: FolderOpen },
    { to: "/widget", label: widgetLabel, icon: Code2 },
    ...pageItems("tools"),
  ];
  const REST = [
    { to: "/sitemap", label: t("nav.sitemap"), icon: Network },
    { to: "/report-bug", label: t("report.link"), icon: Bug },
    ...pageItems("navbar"),
  ];

  const renderLink = (n) => {
    const active = loc.pathname === lp(n.to);
    return (
      <Link key={n.to} to={lp(n.to)} data-testid={`nav-${n.to.slice(1)}`}
        className={cn("flex items-center gap-1.5 whitespace-nowrap rounded-full px-2.5 py-2 text-[13px] font-500 leading-none transition-colors", active ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-accent hover:text-foreground")}>
        <n.icon className="h-4 w-4 shrink-0" /> {n.label}
      </Link>
    );
  };
  const drawerItem = (n) => {
    const active = n.to && loc.pathname === lp(n.to);
    const color = n.color || "#2563eb";
    const inner = (
      <>
        <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl" style={{ backgroundColor: `${color}1a`, color }}>
          <n.icon className="h-5 w-5" />
        </span>
        <div className="min-w-0 flex-1">
          <div className={cn("font-head text-[15px] font-700", active && "text-primary")}>{n.label}</div>
          {n.sub && <div className="truncate text-xs text-muted-foreground">{n.sub}</div>}
        </div>
        <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
      </>
    );
    const cls = cn("flex w-full items-center gap-3 rounded-2xl border p-3.5 text-left transition-all active:scale-[0.98]", active ? "border-primary bg-primary/5" : "border-border hover:bg-accent");
    if (n.action === "logout") {
      return (
        <button key="logout" data-testid="mnav-logout" onClick={() => { logout(); nav(lp("/")); setOpen(false); }} className={cls}>{inner}</button>
      );
    }
    return (
      <Link key={n.to} to={lp(n.to)} onClick={() => setOpen(false)} data-testid={`mnav-${n.to.slice(1).replace(/\//g, "-") || "home"}`} className={cls}>{inner}</Link>
    );
  };

  const drawerGroups = [
    { title: pick(MENU.gNetwork), items: [
      { to: "/test", label: t("nav.speed"), sub: t("diag.sub"), icon: Gauge, color: "#2563eb" },
      { to: "/stability", label: t("nav.stability"), sub: t("stab.sub"), icon: Activity, color: "#16a34a" },
      { to: "/bufferbloat", label: t("nav.bloat"), sub: t("bloat.sub"), icon: Waves, color: "#7c3aed" },
      { to: "/mobile", label: t("nav.mobile"), sub: t("mob.sub"), icon: Smartphone, color: "#2563eb" },
      { to: "/rankings", label: t("nav.ranking"), sub: t("grank.sub"), icon: Trophy, color: "#d97706" },
    ] },
    { title: pick(MENU.gWebsite), items: [
      { to: "/website-speed", label: pick(PL.siteSpeed), sub: pick(MENU.sSiteSpeed), icon: Globe2, color: "#7c3aed" },
      { to: "/sites/compare", label: t("nav.compare"), sub: pick(MENU.sCompare), icon: GitCompareArrows, color: "#0891b2" },
      { to: "/sites/catalog", label: t("nav.saved"), sub: pick(MENU.sSaved), icon: FolderOpen, color: "#d97706" },
      { to: "/widget", label: widgetLabel, sub: pick(MENU.sWidget), icon: Code2, color: "#16a34a" },
      ...customPages
        .filter((p) => (p.placements || []).some((x) => ["tools", "navbar", "mobile"].includes(x)))
        .map((p) => ({ to: `/p/${p.slug}`, label: p.title, sub: "", icon: FileText, color: "#64748b" })),
    ] },
    { title: pick(MENU.gAccount), items: user ? [
      { to: "/history", label: t("nav.panel"), sub: pick(MENU.sOverview), icon: LayoutDashboard, color: "#2563eb" },
      { to: "/history/settings", label: pick(PL.settings), sub: pick(MENU.sSettings), icon: Settings, color: "#64748b" },
      ...(user.role === "admin" ? [{ to: "/admin", label: t("nav.admin"), sub: pick(MENU.sAdmin), icon: ShieldCheck, color: "#7c3aed" }] : []),
      { to: "/sitemap", label: t("nav.sitemap"), sub: pick({ pl: "Wszystkie strony serwisu", en: "All site pages", de: "Alle Seiten", es: "Todas las páginas", fr: "Toutes les pages", it: "Tutte le pagine", pt: "Todas as páginas", tr: "Tüm sayfalar", ru: "Все страницы", ar: "كل الصفحات" }), icon: Network, color: "#0ea5e9" },
      { to: "/report-bug", label: t("report.link"), sub: t("report.subtitle"), icon: Bug, color: "#ef4444" },
      { action: "logout", label: t("nav.logout"), sub: pick(MENU.sLogout), icon: LogOut, color: "#ef4444" },
    ] : [
      { to: "/login", label: t("nav.login"), sub: pick(MENU.sLogin), icon: LayoutDashboard, color: "#2563eb" },
      { to: "/register", label: t("nav.register"), sub: pick(MENU.sRegister), icon: House, color: "#16a34a" },
      { to: "/sitemap", label: t("nav.sitemap"), sub: pick({ pl: "Wszystkie strony serwisu", en: "All site pages", de: "Alle Seiten", es: "Todas las páginas", fr: "Toutes les pages", it: "Tutte le pagine", pt: "Todas as páginas", tr: "Tüm sayfalar", ru: "Все страницы", ar: "كل الصفحات" }), icon: Network, color: "#0ea5e9" },
      { to: "/report-bug", label: t("report.link"), sub: t("report.subtitle"), icon: Bug, color: "#ef4444" },
    ] },
  ];

  return (
    <>
    <header className="sticky top-0 z-50 w-full border-b border-border/60 bg-background/80 backdrop-blur-xl">
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 md:px-8">
        <Link to={lp("/")} data-testid="logo-link" className="flex shrink-0 items-center gap-2">
          <img src="/favicon-96x96.png" alt="freetestnet.com" width="36" height="36" className="h-9 w-9 rounded-xl" />
          <span className="font-head text-xl font-700 tracking-tight">Freetestnet<span className="text-primary">.com</span></span>
        </Link>

        <nav className="hidden items-center gap-0.5 xl:flex">
          <NavDropdown id="network" label={t("nav.network")} icon={Gauge} items={NET} loc={loc} lp={lp} />
          <NavDropdown id="tools" label={t("nav.tools")} icon={FileSearch} items={TOOLS} loc={loc} lp={lp} />
          {REST.map(renderLink)}
        </nav>

        <div className="hidden items-center gap-2 xl:flex">
          <AccessibilityPanel />
          <ThemeToggle />
          <LangSwitch />
          {user ? (
            <>
              {user.role === "admin" && (
                <Link to={lp("/admin")} data-testid="nav-admin" className="flex items-center gap-2 rounded-full border border-primary/40 px-4 py-2 text-sm font-600 text-primary transition-colors hover:bg-primary/10">
                  <ShieldCheck className="h-4 w-4" /> {t("nav.admin")}
                </Link>
              )}
              <Link to={lp("/history")} data-testid="user-panel-btn" className="flex items-center gap-2 rounded-full bg-primary px-4 py-2 text-sm font-600 text-primary-foreground transition-transform hover:-translate-y-0.5">
                <LayoutDashboard className="h-4 w-4" /> {t("nav.panel")}
              </Link>
              <div className="flex items-center gap-2 rounded-full border border-border py-1 pl-1 pr-3">
                <Avatar user={user} />
                <span className="max-w-[100px] truncate text-sm font-500">{user.name}</span>
              </div>
              <button data-testid="logout-btn" onClick={() => { logout(); nav(lp("/")); }} title={t("nav.logout")} aria-label={t("nav.logout")}
                className="flex items-center gap-2 rounded-full border border-border p-2 text-sm text-muted-foreground transition-colors hover:bg-accent hover:text-foreground">
                <LogOut className="h-4 w-4" />
              </button>
            </>
          ) : (
            <>
              <Link to={lp("/login")} data-testid="nav-login" className="rounded-full px-4 py-2 text-sm font-500 text-muted-foreground hover:text-foreground">{t("nav.login")}</Link>
              <Link to={lp("/register")} data-testid="nav-register" className="rounded-full bg-primary px-5 py-2 text-sm font-600 text-primary-foreground transition-transform hover:-translate-y-0.5">{t("nav.register")}</Link>
            </>
          )}
        </div>

        <div className="flex items-center gap-2 xl:hidden">
          <AccessibilityPanel />
          <ThemeToggle />
          <button data-testid="mobile-menu-btn" onClick={() => setOpen(!open)} aria-label={open ? t("nav.closeMenu") : t("nav.menu")} aria-expanded={open} className="flex h-10 w-10 items-center justify-center rounded-full border border-border text-muted-foreground">
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>
    </header>

      {open && (
        <div className="fixed inset-0 z-[60] xl:hidden" data-testid="mobile-drawer">
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm animate-in fade-in" onClick={() => setOpen(false)} />
          <div className="absolute inset-y-0 left-0 flex w-[88%] max-w-sm flex-col bg-background shadow-2xl animate-in slide-in-from-left duration-300">
            <div className="flex items-center justify-between gap-2 border-b border-border px-4 py-4">
              <Link to={lp("/")} onClick={() => setOpen(false)} className="flex min-w-0 items-center gap-2">
                <img src="/favicon-96x96.png" alt="freetestnet.com" width="36" height="36" className="h-9 w-9 shrink-0 rounded-xl" />
                <span className="truncate font-head text-lg font-700 tracking-tight">Freetestnet<span className="text-primary">.com</span></span>
              </Link>
              <div className="flex shrink-0 items-center gap-2">
                <LangSwitch align="right" />
                <button data-testid="mobile-drawer-close" onClick={() => setOpen(false)} aria-label={t("nav.closeMenu")}
                  className="flex h-10 w-10 items-center justify-center rounded-full border border-border text-muted-foreground transition-colors hover:bg-accent">
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>

            <div className="flex-1 space-y-6 overflow-y-auto px-4 py-5">
              <Link to={lp("/install")} onClick={() => setOpen(false)} data-testid="drawer-install"
                className="flex items-center gap-3 rounded-2xl border border-primary/30 bg-primary/5 p-3.5 transition-all active:scale-[0.98]">
                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-primary text-primary-foreground"><Download className="h-5 w-5" /></span>
                <div className="min-w-0 flex-1">
                  <div className="font-head text-[15px] font-800 text-primary">{pick(MENU.installTitle)}</div>
                  <div className="truncate text-xs text-muted-foreground">{pick(MENU.installSub)}</div>
                </div>
                <ChevronRight className="h-4 w-4 shrink-0 text-primary" />
              </Link>

              {drawerGroups.map((g) => (
                <div key={g.title}>
                  <div className="px-1 text-[11px] font-800 uppercase tracking-widest text-muted-foreground/70">{g.title}</div>
                  <div className="mt-2.5 space-y-2.5">{g.items.map(drawerItem)}</div>
                </div>
              ))}

              <div className="relative overflow-hidden rounded-2xl border border-primary/20 bg-gradient-to-br from-primary/10 to-primary/5 p-4">
                <div className="max-w-[75%]">
                  <div className="font-head text-lg font-800">{pick(MENU.ctaTitle)}</div>
                  <p className="mt-1 text-sm text-muted-foreground">{pick(MENU.ctaDesc)}</p>
                  <Link to={lp("/test")} onClick={() => setOpen(false)} data-testid="drawer-cta-btn"
                    className="mt-3 inline-flex items-center gap-2 rounded-full bg-primary px-5 py-2.5 text-sm font-700 text-primary-foreground transition-transform hover:-translate-y-0.5">
                    {pick(MENU.ctaBtn)} <Rocket className="h-4 w-4" />
                  </Link>
                </div>
                <Rocket className="pointer-events-none absolute -bottom-3 -right-2 h-24 w-24 rotate-12 text-primary/15" strokeWidth={1.5} />
              </div>
            </div>

            <div className="flex items-center gap-3 border-t border-border px-4 py-3">
              <div className="flex items-center gap-2 text-sm font-600 text-muted-foreground">
                <ThemeToggle /> <span>{pick(MENU.theme)}</span>
              </div>
              <AccessibilityPanel openUp showLabel />
            </div>
          </div>
        </div>
      )}
    </>
  );
}
